import { useState, useRef } from 'react';
import { formatCurrency } from '@atc/string-fns';
import 'prop-types';
import { SideBarToModal, Text, Button, ErrorMessages, LoadingSpinner } from 'reaxl';
import { Form, StandardZipInput } from 'reaxl-forms';
import { useDevice } from '@atc/react-device';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
const getCustomFees = taxesFees => {
  const excludedKeys = ['totalTaxesFees', 'totalTax', 'showTaxTotalOnly', 'dealerFees', 'dmvFees', 'lenderFees', 'capCost', 'dueInAdvance'];
  return Object.entries(taxesFees).filter(_ref => {
    let [key] = _ref;
    return !excludedKeys.includes(key);
  }).map(_ref2 => {
    let [key, value] = _ref2;
    // Format the label properly with capitalization
    let label;
    if (key.includes(' ')) {
      // For keys with spaces, capitalize each word
      label = key.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
    } else {
      // For camelCase, add spaces and capitalize first letter
      label = key.replace(/([A-Z])/g, ' $1').replace(/^(.)/, s => s.toUpperCase());
    }
    return {
      key,
      label,
      value
    };
  });
};

/**
 * TaxesFeesModal component displays detailed tax and fee information for vehicle purchases or leases.
 *
 * This modal allows users to confirm their location via ZIP code and view a breakdown of all applicable
 * taxes and fees. It provides different views based on whether the user is in finance or lease mode.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} [props.activeTab='finance'] - Current active tab ('finance' or 'lease')
 * @param {HTMLElement} [props.container] - Container element for syndicated calculator implementation
 * @param {string} props.zip - User's ZIP code for tax calculation
 * @param {Object} [props.taxesFees={}] - Object containing tax and fee information
 * @param {Function} [props.onHide=()=>{}] - Callback function when modal is closed
 * @param {Function} [props.onContinue=()=>{}] - Callback function when user continues with the flow
 * @param {Function} [props.onUpdate=()=>{}] - Callback function when ZIP code is updated
 * @param {string} props.isEstimate - Determines if the 'Est." label is shown
 * @param {boolean} [props.showModal=false] - Controls visibility of the modal
 * @param {boolean} [props.isLoading=false] - Indicates if data is being loaded
 * @param {boolean} [props.hasError=false] - Indicates if there was an error loading data
 * @returns {React.Component} - The TaxesFeesModal component
 */
function TaxesFeesModal(_ref3) {
  let {
    activeTab = 'finance',
    container,
    // For Syndicated Calc
    zip,
    taxesFees = {},
    onHide = () => {},
    onContinue = () => {},
    onUpdate = () => {},
    isEstimate,
    showModal = false,
    isLoading = false,
    hasError = false
  } = _ref3;
  const device = useDevice();
  const isXs = device?.is?.xs ?? false;
  const [hasUpdatedZip, setHasUpdatedZip] = useState(false); // State to track if the ZIP code has been updated from each reference of the modal
  const userZip = useRef(zip);
  const isLease = activeTab === 'lease';
  const {
    capCost = {},
    dueInAdvance = {}
  } = taxesFees;
  const {
    capitalizedDealerFees,
    capitalizedGovFees,
    capitalizedLenderFees,
    capitalizedTaxes,
    capitalizedTaxesAndFees
  } = capCost;
  const {
    diaDealerFees,
    diaGovFees,
    diaLenderFees,
    diaTaxes
  } = dueInAdvance;
  const totalDiaTaxesFees = (diaTaxes ?? 0) + (diaLenderFees ?? 0) + (diaGovFees ?? 0) + (diaDealerFees ?? 0);
  const showTotalTaxesOnly = !!taxesFees.showTaxTotalOnly; // for Esntial UDS only

  const handleSubmit = event => {
    onHide();
    onContinue(hasUpdatedZip ? userZip.current : zip, event);
    setHasUpdatedZip(false);
  };
  const handleModalHide = () => {
    onHide();
    setHasUpdatedZip(false);
  };
  const handleChange = e => {
    const updatedZip = e?.inputZip;
    if (updatedZip.length === 5) {
      setHasUpdatedZip(true);
      userZip.current = updatedZip;
      onUpdate(updatedZip);
    }
  };
  const continueButton = /*#__PURE__*/jsx(Button, {
    type: "button",
    bsStyle: "primary",
    onClick: handleSubmit,
    disabled: hasError || isLoading,
    children: "Apply Taxes & Fees"
  });
  const renderTotalTaxesFees = (label, total, classname) => !showTotalTaxesOnly && !!total && /*#__PURE__*/jsxs("div", {
    className: classname,
    children: [/*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: label
    }), /*#__PURE__*/jsx(Text, {
      weight: "bold",
      children: `${formatCurrency(Math.floor(total))}`
    })]
  });
  const renderFeesList = (fees, classnames) => fees.map(_ref4 => {
    let {
      key,
      label,
      value
    } = _ref4;
    return !!value && /*#__PURE__*/jsxs("li", {
      className: classnames,
      children: [/*#__PURE__*/jsx("span", {
        children: label
      }), /*#__PURE__*/jsx(Text, {
        children: `+ ${formatCurrency(Math.floor(value))}`
      })]
    }, key);
  });
  const renderFeeSection = function (title, feesList, totalLabel, totalAmount) {
    let className = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : '';
    return /*#__PURE__*/jsxs("ul", {
      className: `list-unstyled margin-bottom-0 ${className}`,
      children: [/*#__PURE__*/jsxs(Text, {
        color: "primary",
        weight: "bold",
        size: 400,
        children: [isEstimate ? 'Estimated ' : '', title]
      }), isLoading && /*#__PURE__*/jsx(LoadingSpinner, {
        className: "margin-vertical-3 margin-left-0"
      }), !isLoading && renderFeesList(feesList, 'display-flex justify-content-between gap-3'), !isLoading && renderTotalTaxesFees(totalLabel, totalAmount, 'display-flex justify-content-between gap-3')]
    });
  };
  const renderTaxesFeesBreakdown = () => {
    if (!isLoading && hasError) {
      return /*#__PURE__*/jsx(ErrorMessages, {
        errors: ["We're sorry. We couldn't calculate taxes & fees for your location. Talk to the dealer about final calculations."]
      });
    }

    // Define fee lists for different payment types
    const financeAndCashFees = [{
      key: 'taxes',
      label: 'Taxes',
      value: taxesFees.totalTax
    }, {
      key: 'lenderFees',
      label: 'Lender Fees',
      value: taxesFees.lenderFees
    }, {
      key: 'dmvFees',
      label: 'DMV Fees',
      value: taxesFees.dmvFees
    }, {
      key: 'dealerFees',
      label: isEstimate ? 'Estimated Dealer Fees' : 'Dealer Fees',
      value: taxesFees.dealerFees
    }];

    // Handle custom keys in taxesFees object (e.g., "Documentation Fee")
    const customFees = getCustomFees(taxesFees);
    if (customFees.length > 0) {
      financeAndCashFees.push(...customFees);
    }
    const leaseDueAtSigningFees = [{
      key: 'diaTaxes',
      label: 'Taxes',
      value: diaTaxes
    }, {
      key: 'diaLenderFees',
      label: 'Lender Fees',
      value: diaLenderFees
    }, {
      key: 'diaGovFees',
      label: 'DMV Fees',
      value: diaGovFees
    }, {
      key: 'diaDealerFees',
      label: 'Dealer Fees',
      value: diaDealerFees
    }];
    const leaseCapitalizedFees = [{
      key: 'capTaxes',
      label: 'Taxes',
      value: capitalizedTaxes
    }, {
      key: 'capLenderFees',
      label: 'Lender Fees',
      value: capitalizedLenderFees
    }, {
      key: 'capGovFees',
      label: 'DMV Fees',
      value: capitalizedGovFees
    }, {
      key: 'capDealerFees',
      label: 'Dealer Fees',
      value: capitalizedDealerFees
    }];
    const totalAmount = showTotalTaxesOnly ? taxesFees?.totalTaxes : taxesFees.totalTaxesFees;
    return /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx("hr", {
        className: "margin-vertical-0"
      }), /*#__PURE__*/jsx("div", {
        className: "margin-top-4",
        children: isLease ? /*#__PURE__*/jsxs(Fragment, {
          children: [(isLoading || !!totalDiaTaxesFees) && renderFeeSection('Due at Signing', leaseDueAtSigningFees, 'Total Due at Signing', totalDiaTaxesFees), (isLoading || !!capitalizedTaxesAndFees) && renderFeeSection('Financed', leaseCapitalizedFees, 'Total Financed', capitalizedTaxesAndFees, 'margin-top-4')]
        }) : renderFeeSection('Financed', financeAndCashFees, 'Total Financed', totalAmount, 'margin-top-4')
      })]
    });
  };
  return /*#__PURE__*/jsx(SideBarToModal, {
    "data-cmp": "taxes-fees-modal",
    title: "Taxes & Fees",
    show: showModal,
    container: container,
    content: /*#__PURE__*/jsxs(Fragment, {
      children: [/*#__PURE__*/jsx(Text, {
        color: "primary",
        weight: "bold",
        size: 400,
        children: "Confirm Your Location"
      }), /*#__PURE__*/jsx("p", {
        children: "Your location will be used to calculate taxes & fees and show more accurate payments."
      }), /*#__PURE__*/jsx("div", {
        className: "margin-top-4",
        children: /*#__PURE__*/jsx(Form, {
          onSubmit: handleChange,
          children: /*#__PURE__*/jsxs("div", {
            className: "display-flex row",
            children: [/*#__PURE__*/jsx("div", {
              className: "col-xs-4",
              children: /*#__PURE__*/jsx(StandardZipInput, {
                className: "margin-bottom-5",
                label: "ZIP Code",
                layout: "inside",
                name: "inputZip",
                id: "inputZip",
                value: zip,
                required: true,
                style: {
                  minWidth: '80px'
                }
              })
            }), /*#__PURE__*/jsx(Button, {
              className: "margin-bottom-5",
              type: "submit",
              children: "Update Taxes & Fees"
            })]
          })
        })
      }), renderTaxesFeesBreakdown()]
    }),
    collapsed: isXs,
    modalOptions: {
      bsStyle: 'sm'
    },
    sideBarOptions: {
      fullWidth: true,
      action: continueButton
    },
    footerContent: continueButton,
    onHide: handleModalHide
  });
}
export { TaxesFeesModal as default };
